import { chain, isEmpty, isNil, keys, values } from 'lodash';
import type { TFunction } from 'react-i18next';
import moment from 'moment';
// Import Types
import type {
    DynamicVariableChannelData,
    TemplateById,
    TemplateInitialData,
    TemplatePlaceholderData,
    WhatsAppBodyComponent,
    WhatsAppPlaceholderMapping,
    WhatsAppTemplateBodyData,
    WhatsappTemplateApiResponse,
} from 'types';
// Import Enums
import {
    ObjectDefinitionTypeEnum,
    TemplateTypeEnum,
    TemplateValueTypeEnum,
    WhatsAppComponentTypeEnum,
    WhatsAppHeaderFormatEnum,
} from 'enums';
// Import Helpers
import {
    customMomentWithoutTimezoneConversion,
    getTranslatableErrorText,
    injectContentUrl,
    replaceContentUrlWithPlaceholder,
} from 'services/Helpers';
import { buildDateTime } from 'components/CustomerJourneyStepper/helpers';
// Import Constants
import { HooryTemplateConfig, ViberTemplateConfig } from './config';
import {
    DynamicPopUpDeviceTypesLabels,
    TemplateInputTypes,
    TemplateTypesLabels,
    l,
    ExpirationTypes,
    MonthDayYearFormat,
    TimeFormatWithoutSeconds,
    ZERO_TIME_WITHOUT_SECONDS,
} from 'constants/common';

function createTemplateResultData(
    Name,
    Description,
    data,
    type,
    eventId,
    templateDynamicVariables,
    definitionType,
    predefinedOptions,
    partnerTimeZone,
) {
    const isPredefined = definitionType === ObjectDefinitionTypeEnum.Predefined;

    return values(data).map((template) => {
        let body = template[TemplateInputTypes.Body];
        const categoryIds = template[TemplateInputTypes.Category];
        const expirationType = template[TemplateInputTypes.ExpirationType] as Nullable<string>;
        const expirationPeriod = template[TemplateInputTypes.ExpirationPeriod] as Nullable<string>;
        const expirationDate = template[TemplateInputTypes.ExpirationDate] as Nullable<string>;
        const expirationTime = template[TemplateInputTypes.ExpirationTime] as Nullable<string>;
        const timezone = (template[TemplateInputTypes.Timezone] as Nullable<string>) ?? partnerTimeZone;

        const ExpirationDate = !isNil(expirationDate) ? buildDateTime(expirationDate, expirationTime) : null;

        if (type === TemplateTypeEnum.DynamicPopUp) {
            body = {
                Content: replaceLinksTarget(body, '_top', '_blank'),
                Page: { Comparison: template[TemplateInputTypes.UrlType], Value: template[TemplateInputTypes.Url] },
                Platforms: template[TemplateInputTypes.DeviceType],
                ...(expirationType === ExpirationTypes.ExpirationPeriod
                    ? {
                          Period: expirationPeriod ? `${expirationPeriod}` : null,
                      }
                    : {}),
                ...(expirationType === ExpirationTypes.ExpirationDate
                    ? {
                          ExpirationDate: ExpirationDate,
                          TimeZone: timezone,
                      }
                    : {}),
            };

            body = JSON.stringify(body);
        }

        let resultBody = replaceContentUrlWithPlaceholder(body);

        if (!isNil(templateDynamicVariables)) {
            resultBody = recoverTopImageUrlPlaceholders(resultBody, templateDynamicVariables, type);
        }

        return {
            ...template,
            Body: resultBody,
            TemplateCategoryIds: categoryIds,
            ...(template.UIConfig && { UIConfig: JSON.stringify(template.UIConfig) }),
            DeliveryMethod: `${TemplateTypesLabels[type]}`,
            Description,
            Name: template.Language ? `${Name} (${template.Language})` : Name,
            EventId: eventId,
            DefinitionType: definitionType,
            PredefinedCategories: isPredefined ? predefinedOptions.categories : null,
            PredefinedRegions: isPredefined ? predefinedOptions.regions : null,
        };
    });
}

const groupDynamicVariablesByDisplayName = (
    channels: { type: TemplateTypeEnum; variables: TemplatePlaceholderData[] }[],
) => {
    const groupedDynamicVariables: DynamicVariableChannelData[] = [];

    channels.forEach((channel) => {
        channel.variables.forEach((dynamicVariable) => {
            const foundVariable = groupedDynamicVariables.find(
                (item) => item.displayNameKey === dynamicVariable.DisplayNameKey,
            );

            if (foundVariable) {
                foundVariable.channels[channel.type] = dynamicVariable;
            } else {
                groupedDynamicVariables.push({
                    displayNameKey: dynamicVariable.DisplayNameKey,
                    channels: {
                        [channel.type]: dynamicVariable,
                    },
                });
            }
        });
    });

    return groupedDynamicVariables;
};

//todo: don't remove until backend endpoint will be changed to all template models
function createPushNotificationTemplateResultData(
    Name,
    Description,
    data,
    type,
    { appTypesValue, deviceTypesValue, routeValue, cascadeValue },
    eventId,
    definitionType,
    predefinedOptions,
) {
    const isPredefined = definitionType === ObjectDefinitionTypeEnum.Predefined;

    const tmpCascadeValue = { ...cascadeValue };
    return values(data).map(
        ({ Language, Body, TitleOfPushNotification, TemplateId, TemplateCategoryIds, UploadedImageUrl, FileName }) => {
            return {
                Language,
                DeliveryMethod: `${TemplateTypesLabels[type]}`,
                Description,
                Name: Language ? `${Name} (${Language})` : Name,
                Body: JSON.stringify({
                    message_title: TitleOfPushNotification,
                    message_body: Body,
                    app_type: appTypesValue,
                    session_source: deviceTypesValue,
                    extra_info: {
                        ...tmpCascadeValue,
                        image_url: UploadedImageUrl,
                        file_name: FileName,
                        notification_routes: routeValue,
                    },
                }),
                EventId: eventId,
                TemplateId: TemplateId,
                TemplateCategoryIds: TemplateCategoryIds,
                DefinitionType: definitionType,
                ...(isPredefined && {
                    PredefinedCategories: predefinedOptions.categories,
                    PredefinedRegions: predefinedOptions.regions,
                }),
            };
        },
    );
}

function createPushNotificationTemplateDefaultData(data, t) {
    return data.reduce((acc, template) => {
        const imageUrl = JSON.parse(template.Body).extra_info.image_url;
        template.ParentTemplateId = null;
        acc[template.Language || 'defaultLang'] = {
            ...template,
            TitleOfPushNotification: JSON.parse(template.Body).message_title,
            Body: JSON.parse(template.Body).message_body,
            UploadedImageUrl: imageUrl,
            FileName: !isEmpty(imageUrl) && !imageUrl.startsWith('{') ? t(l.File) : '',
            isDeletable: !(template?.IsUsed ?? false),
        };

        return acc;
    }, {});
}

function createPopUpNotificationTemplateResultData(
    Name,
    Description,
    data,
    type,
    { popUpTypeValue, urlTypeValue, urlValue, popUpTypesData },
    eventId,
    categoryValue,
) {
    const tmpPopUpType = popUpTypesData.find((item) => item.value === popUpTypeValue);

    return values(data).map(({ Language, TemplateId }) => {
        return {
            DeliveryMethod: `${TemplateTypesLabels[type]}`,
            Description,
            Name: Language ? `${Name} (${Language})` : Name,
            Body: JSON.stringify({
                Alias: tmpPopUpType?.Alias,
                Page: { Comparison: urlTypeValue, Value: urlValue },
                Id: popUpTypeValue,
            }),
            TemplateId: TemplateId,
            TemplateCategoryIds: categoryValue,
            EventId: eventId,
        };
    });
}

function htmlStringToDomElement(htmlText) {
    const domParser = new DOMParser();
    return domParser.parseFromString(htmlText, 'text/html');
}

function replaceLinksTarget(htmlText, targetType, excludeType) {
    const lastClosingTag = htmlText.match(/<\/([A-z]{3,})>$/)?.[1];
    const htmlDocument = htmlStringToDomElement(htmlText);
    const links = htmlDocument.querySelectorAll(`a:not([target='${excludeType}'])`);

    links.forEach((link) => {
        link.setAttribute('target', targetType);
    });

    switch (lastClosingTag) {
        case 'html':
            return htmlDocument.documentElement.outerHTML;
        case 'body':
            return htmlDocument.body.outerHTML;
        default:
            return htmlDocument.body.innerHTML;
    }
}

function generatePartialHtml(htmlTextOrDocument) {
    const htmlDocument =
        typeof htmlTextOrDocument === 'string' ? htmlStringToDomElement(htmlTextOrDocument) : htmlTextOrDocument;
    const wrapper = document.createElement('div');
    const styles = htmlDocument.head.querySelectorAll('style');

    // Apply Body attributes to the wrapper to keep appearance consistent
    htmlDocument.body.getAttributeNames().forEach((attributeName) => {
        const attributeValue = htmlDocument.body.getAttribute(attributeName);

        wrapper.setAttribute(attributeName, attributeValue);
    });
    styles.forEach((styles) => wrapper.appendChild(styles));

    wrapper.innerHTML = htmlDocument.body.innerHTML;

    return wrapper.outerHTML;
}

function createCMSPopUpNotificationTemplateResultData(name, description, data, popUpId, selectedCategory) {
    return values(data).map(({ Language, TemplateId }) => {
        return {
            DeliveryMethod: `${TemplateTypesLabels[TemplateTypeEnum.CMSPopUp]}`,
            Description: description,
            Name: Language ? `${name} (${Language})` : name,
            Body: JSON.stringify({ Alias: popUpId, Id: popUpId }),
            TemplateCategoryIds: selectedCategory,
            TemplateId: TemplateId,
        };
    });
}

function createCMSBannerTemplateResultData(name, description, selectedBanner, templateId) {
    const {
        rowKey: _rowKey,
        isRowSelected: _isRowSelected,
        isManuallySelected: _isManuallySelected,
        ...bannerData
    } = selectedBanner ?? {};
    return [
        {
            DeliveryMethod: `${TemplateTypesLabels[TemplateTypeEnum.CMSBanner]}`,
            Description: description,
            Name: name,
            Body: JSON.stringify(bannerData),
            ...(templateId ? { TemplateId: templateId } : {}),
        },
    ];
}

function createViberNotificationTemplateResultData(
    Name,
    Description,
    data,
    type,
    isCascade,
    EventId,
    definitionType,
    predefinedOptions,
) {
    const isPredefined = definitionType === ObjectDefinitionTypeEnum.Predefined;

    return values(data).map((template) => {
        const {
            Body,
            Language,
            UploadedImageUrl,
            ButtonText,
            ButtonUrl,
            SMSBody,
            TemplateId,
            ValidityPeriod,
            TemplateCategoryIds,
        } = template;
        return {
            Language,
            DeliveryMethod: `${TemplateTypesLabels[type]}`,
            Description,
            Name: Language ? `${Name} (${Language})` : Name,
            Body: JSON.stringify({
                Body: Body,
                ImageUrl: UploadedImageUrl,
                InlineKeyboard: isNil(ButtonText) || isNil(ButtonUrl) ? [] : [[{ Text: ButtonText, Url: ButtonUrl }]],
                Schedule: {
                    ValidityPeriod: ValidityPeriod,
                },
                ...(isCascade && { SMSBody: SMSBody ?? '' }),
            }),
            TemplateId: TemplateId,
            EventId: EventId,
            TemplateCategoryIds: TemplateCategoryIds,
            DefinitionType: definitionType,
            ...(isPredefined && {
                PredefinedCategories: predefinedOptions.categories,
                PredefinedRegions: predefinedOptions.regions,
            }),
        };
    });
}

function createViberNotificationTemplateDefaultData(data, isCascade, t) {
    return data.reduce((acc, template) => {
        const parsedBody = JSON.parse(template.Body);

        const button = parsedBody?.InlineKeyboard?.[0]?.[0] ?? { Text: '', Url: '' };

        template.ParentTemplateId = null;

        acc[template.Language || 'defaultLang'] = {
            ...template,
            Body: parsedBody.Body,
            ValidityPeriod: parsedBody.Schedule?.ValidityPeriod,
            ButtonText: button.Text,
            ButtonUrl: button.Url,
            UploadedImageUrl: parsedBody.ImageUrl,
            isDeletable: !(template?.IsUsed ?? false),
            FileName: parsedBody.ImageUrl === '' ? '' : t(l.File),
            ...(isCascade && { SMSBody: parsedBody.SMSBody ?? '' }),
        };
        return acc;
    }, {});
}

function createHooryNotificationTemplateResultData(
    Name,
    Description,
    data,
    type,
    EventId,
    definitionType,
    predefinedOptions,
) {
    const isPredefined = definitionType === ObjectDefinitionTypeEnum.Predefined;

    return values(data).map((template) => {
        const {
            Body,
            Subject,
            Language,
            UploadedImageUrl,
            ButtonText,
            ButtonUrl,
            TemplateId,
            ValidityPeriod,
            TemplateCategoryIds,
        } = template;
        return {
            Language,
            DeliveryMethod: `${TemplateTypesLabels[type]}`,
            Description,
            Name: Language ? `${Name} (${Language})` : Name,
            Subject,
            Body: JSON.stringify({
                Body: Body,
                ImageUrl: UploadedImageUrl,
                InlineKeyboard: isNil(ButtonText) || isNil(ButtonUrl) ? [] : [[{ Text: ButtonText, Url: ButtonUrl }]],
                Schedule: {
                    ValidityPeriod: ValidityPeriod,
                },
            }),
            TemplateId: TemplateId,
            EventId: EventId,
            TemplateCategoryIds: TemplateCategoryIds,
            DefinitionType: definitionType,
            ...(isPredefined && {
                PredefinedCategories: predefinedOptions.categories,
                PredefinedRegions: predefinedOptions.regions,
            }),
        };
    });
}

function createHooryNotificationTemplateDefaultData(data, t) {
    return data.reduce((acc, template) => {
        const parsedBody = JSON.parse(template.Body);

        const button = parsedBody?.InlineKeyboard?.[0]?.[0] ?? { Text: '', Url: '' };

        template.ParentTemplateId = null;

        acc[template.Language || 'defaultLang'] = {
            ...template,
            Body: parsedBody.Body,
            ValidityPeriod: parsedBody.Schedule?.ValidityPeriod,
            ButtonText: button.Text,
            ButtonUrl: button.Url,
            UploadedImageUrl: parsedBody.ImageUrl,
            isDeletable: !(template?.IsUsed ?? false),
            FileName: parsedBody.ImageUrl === '' ? '' : t(l.File),
        };
        return acc;
    }, {});
}

function createWhatsAppProviderNotificationTemplateResultData(
    Name,
    Description,
    data,
    type,
    isCascade,
    EventId,
    definitionType,
    predefinedOptions,
) {
    const isPredefined = definitionType === ObjectDefinitionTypeEnum.Predefined;

    return values(data).map(
        ({ Body, Language, UploadedImageUrl, ButtonText, ButtonUrl, SMSBody, TemplateId, TemplateCategoryIds }) => {
            return {
                Language,
                DeliveryMethod: `${TemplateTypesLabels[type]}`,
                Description,
                Name: Language ? `${Name} (${Language})` : Name,
                Body: JSON.stringify({
                    Body: Body,
                    ImageUrl: UploadedImageUrl,
                    InlineKeyboard:
                        isNil(ButtonText) || isNil(ButtonUrl) ? [] : [[{ Text: ButtonText, Url: ButtonUrl }]],
                    ...(isCascade && { SMSBody: SMSBody ?? '' }),
                }),
                TemplateId: TemplateId,
                EventId: EventId,
                TemplateCategoryIds: TemplateCategoryIds,
                DefinitionType: definitionType,
                ...(isPredefined && {
                    PredefinedCategories: predefinedOptions.categories,
                    PredefinedRegions: predefinedOptions.regions,
                }),
            };
        },
    );
}

function createWhatsAppProviderNotificationTemplateDefaultData(data, isCascade, t) {
    return data.reduce((acc, template) => {
        const parsedBody = JSON.parse(template.Body);
        template.ParentTemplateId = null;

        const button = parsedBody?.InlineKeyboard?.[0]?.[0] ?? { Text: '', Url: '' };

        acc[template.Language || 'defaultLang'] = {
            ...template,
            Body: parsedBody.Body,
            ButtonText: button.Text,
            ButtonUrl: button.Url,
            UploadedImageUrl: parsedBody.ImageUrl,
            isDeletable: !(template?.IsUsed ?? false),
            FileName: parsedBody.ImageUrl === '' ? '' : t(l.File),
            ...(isCascade && { SMSBody: parsedBody.SMSBody ?? '' }),
        };
        return acc;
    }, {});
}

function buildWhatsAppComponents(templateForLanguage, placeholderMapping) {
    const components = templateForLanguage?.Components ?? [];
    if (!components.length) return [];
    const mapping = placeholderMapping ?? {};
    return components
        .filter((comp) => comp?.Example?.Params?.length > 0)
        .map((comp) => {
            const params = comp?.Example?.Params ?? [];
            const compType = comp.Type;
            const compFormat = comp.Format?.toUpperCase();
            const isImageHeader =
                compType === WhatsAppComponentTypeEnum.Header && compFormat === WhatsAppHeaderFormatEnum.Image;
            const parameters = params.map((param, i) => ({
                Name: param?.ParamName ?? null,
                Value: isImageHeader
                    ? (param?.ParamExample ?? '')
                    : (mapping[compType]?.[i]?.dynamicVariableFieldName ?? ''),
            }));
            return {
                Type: compType,
                SubType: comp.SubType ?? null,
                Index: comp.Index != null ? String(comp.Index) : null,
                Format: comp.Format ?? null,
                Parameters: parameters,
            };
        });
}

function createWhatsAppNotificationTemplateResultData(
    Name: string,
    Description: string,
    data: Record<string, AnyObject>,
    type: TemplateTypeEnum,
    EventId: number | null,
    templatesByLanguage: WhatsappTemplateApiResponse[] | null,
) {
    const templateName = (data?.defaultLang?.[TemplateInputTypes.ExternalWhatsAppTemplateId] as string | null) ?? null;

    return values(data).map(
        ({
            Language,
            [TemplateInputTypes.ExternalWhatsAppTemplateLanguage]: WhatsAppTemplateLanguage,
            [TemplateInputTypes.ExternalWhatsAppTemplatePlaceholderMapping]: WhatsAppPlaceholderMapping,
            TemplateId,
            TemplateCategoryIds,
        }) => {
            const templateLanguage = (WhatsAppTemplateLanguage as string) ?? Language ?? null;
            let components: WhatsAppBodyComponent[] = [];
            if (templateName && templateLanguage && templatesByLanguage?.length) {
                const templateForLanguage = templatesByLanguage.find(
                    (template) => template.Language === templateLanguage,
                );
                components = buildWhatsAppComponents(
                    templateForLanguage,
                    WhatsAppPlaceholderMapping as WhatsAppPlaceholderMapping,
                );
            }
            const body = {
                TemplateName: templateName,
                TemplateLanguage: templateLanguage,
                Components: components,
            };
            return {
                Language,
                DeliveryMethod: `${TemplateTypesLabels[type]}`,
                Description,
                Name: Language ? `${Name} (${Language})` : Name,
                Body: JSON.stringify(body),
                TemplateId,
                EventId,
                TemplateCategoryIds,
            };
        },
    );
}

function createWhatsAppNotificationTemplateDefaultData(
    data: Array<{ template: TemplateById; parsedBody: WhatsAppTemplateBodyData }>,
    _t: TFunction,
): Record<string, TemplateInitialData> {
    return data.reduce<Record<string, TemplateInitialData>>((acc, { template, parsedBody }) => {
        const templateWithNullParent: TemplateById = { ...template, ParentTemplateId: null };

        const components: WhatsAppBodyComponent[] = parsedBody.Components ?? [];
        const placeholderMapping: WhatsAppPlaceholderMapping | null = components.length
            ? components.reduce<WhatsAppPlaceholderMapping>((mapping, comp) => {
                  mapping[comp.Type] = (comp.Parameters ?? []).map((param) => ({
                      paramKey: param?.Name ?? '',
                      dynamicVariableFieldName: param?.Value ?? null,
                  }));
                  return mapping;
              }, {})
            : null;

        acc[template.Language || 'defaultLang'] = {
            ...templateWithNullParent,
            UIConfig: undefined,
            [TemplateInputTypes.ExternalWhatsAppTemplateId]: parsedBody.TemplateName,
            [TemplateInputTypes.ExternalWhatsAppTemplateLanguage]: parsedBody.TemplateLanguage,
            [TemplateInputTypes.ExternalWhatsAppTemplatePlaceholderMapping]: placeholderMapping,
            isDeletable: !(template?.IsUsed ?? false),
        };
        return acc;
    }, {});
}

function createTelegramNotificationTemplateResultData(
    Name,
    Description,
    data,
    type,
    EventId,
    definitionType,
    predefinedOptions,
) {
    const isPredefined = definitionType === ObjectDefinitionTypeEnum.Predefined;

    return values(data).map(({ Body, Language, UploadedImageUrl, InlineKeyboard, TemplateId, TemplateCategoryIds }) => {
        return {
            Language,
            DeliveryMethod: `${TemplateTypesLabels[type]}`,
            Description,
            Name: Language ? `${Name} (${Language})` : Name,
            Body: JSON.stringify({
                Text: Body,
                MediaUrl: UploadedImageUrl,
                InlineKeyboard: InlineKeyboard.filter((row) => row.length > 0).map((row) =>
                    row.map((button) => ({
                        Text: button.text,
                        Url: button.url,
                    })),
                ),
            }),
            TemplateCategoryIds: TemplateCategoryIds,
            TemplateId: TemplateId,
            EventId: EventId,
            DefinitionType: definitionType,
            ...(isPredefined && {
                PredefinedCategories: predefinedOptions.categories,
                PredefinedRegions: predefinedOptions.regions,
            }),
        };
    });
}

function createTelegramNotificationTemplateDefaultData(data, t) {
    return data.reduce((acc, template) => {
        const parsedBody = JSON.parse(template.Body);
        template.ParentTemplateId = null;
        acc[template.Language || 'defaultLang'] = {
            ...template,
            Body: parsedBody.Text,
            InlineKeyboard: isEmpty(parsedBody.InlineKeyboard)
                ? [[]]
                : parsedBody.InlineKeyboard.map((row) => row.map((button) => ({ url: button.Url, text: button.Text }))),
            InlineKeyboardFormikValidity: null,
            UploadedImageUrl: parsedBody.MediaUrl,
            isDeletable: !(template?.IsUsed ?? false),
            FileName: parsedBody.MediaUrl === '' ? '' : t(l.File),
        };
        return acc;
    }, {});
}

function createPopUpNotificationTemplateDefaultData(data) {
    return data.reduce((acc, template) => {
        template.ParentTemplateId = null;
        acc[template.Language || 'defaultLang'] = {
            ...template,
            TitleOfPopUpNotification: JSON.parse(template.Body).message_title,
            Body: JSON.parse(template.Body).message_body,
            isDeletable: !(template?.IsUsed ?? false),
        };
        return acc;
    }, {});
}

function createCMSPopUpNotificationTemplateDefaultData(data) {
    return data.reduce((acc, template) => {
        template.ParentTemplateId = null;
        acc[template.Language || 'defaultLang'] = {
            ...template,
            Body: JSON.parse(template.Body),
        };
        return acc;
    }, {});
}

function createCMSBannerTemplateDefaultData(data) {
    return data.reduce((acc, template) => {
        template.ParentTemplateId = null;
        acc[template.Language || 'defaultLang'] = {
            ...template,
            Body: JSON.parse(template.Body),
        };
        return acc;
    }, {});
}

function createTemplateDefaultData(data) {
    return data.reduce((acc, template) => {
        template.ParentTemplateId = null;
        acc[template.Language || 'defaultLang'] = {
            ...template,
            UIConfig: JSON.parse(template.UIConfig),
            isDeletable: !(template?.IsUsed ?? false),
            Body: injectContentUrl(template?.Body),
        };
        return acc;
    }, {});
}

export const getExpirationData = (data: AnyObject, partnerTimeZone: number, partnerDateTimeFormat?: string) => {
    const tmpPeriod: string = data.Period;
    const tmpDate: string = data.ExpirationDate;
    const timezone: string = data.TimeZone ?? partnerTimeZone;
    const expirationType = !isNil(tmpDate)
        ? ExpirationTypes.ExpirationDate
        : !isNil(tmpPeriod)
          ? ExpirationTypes.ExpirationPeriod
          : null;

    const expirationPeriod = !isNil(tmpPeriod) ? `${tmpPeriod}` : null;
    const expirationDate =
        tmpDate && moment(tmpDate).isValid()
            ? customMomentWithoutTimezoneConversion(tmpDate).format(MonthDayYearFormat)
            : null;

    const expirationTime = !isNil(expirationDate)
        ? customMomentWithoutTimezoneConversion(tmpDate).format(TimeFormatWithoutSeconds)
        : ZERO_TIME_WITHOUT_SECONDS;

    const expirationDateTime = !isNil(expirationDate)
        ? customMomentWithoutTimezoneConversion(tmpDate).format(partnerDateTimeFormat)
        : null;

    return {
        expirationType,
        expirationPeriod,
        expirationDateTime,
        expirationDate,
        expirationTime,
        timezone,
    };
};

function createDynamicPopUpTemplateDefaultData(
    data,
    expirationType,
    expirationPeriod,
    expirationDate,
    expirationTime,
    timezone,
) {
    return data.reduce((acc, template) => {
        const parsedBody = JSON.parse(template.Body);
        template.ParentTemplateId = null;
        acc[template.Language || 'defaultLang'] = {
            ...template,
            [TemplateInputTypes.Body]: parsedBody.Content,
            [TemplateInputTypes.DeviceType]: parsedBody.Platforms,
            [TemplateInputTypes.UrlType]: parsedBody.Page.Comparison,
            [TemplateInputTypes.Url]: parsedBody.Page.Value,
            [TemplateInputTypes.ExpirationType]: expirationType,
            [TemplateInputTypes.ExpirationPeriod]: expirationPeriod,
            [TemplateInputTypes.ExpirationDate]: expirationDate,
            [TemplateInputTypes.ExpirationTime]: expirationTime,
            [TemplateInputTypes.Timezone]: timezone,
            UIConfig: JSON.parse(template.UIConfig),
            isDeletable: !(template?.IsUsed ?? false),
        };
        return acc;
    }, {});
}

function createLanguageModalData(data, selectedItems) {
    return data.reduce((acc, { Name, Id }, index) => {
        if (!selectedItems.includes(Id)) {
            acc.push({
                hasHover: true,
                dragDisable: true,
                nestedData: [],
                data: {
                    isRowSelected: selectedItems.includes(Id),
                    index,
                    Language: Name,
                    Id,
                },
            });
        }
        return acc;
    }, []);
}

const createSelectLanguageModalData = (data, selectedItems) => {
    return data.reduce((acc, { Name, Id }, index) => {
        acc.push({
            hasHover: true,
            dragDisable: true,
            nestedData: [],
            data: {
                isRowSelected: selectedItems.includes(Id),
                index,
                Language: Name,
                Id,
            },
        });
        return acc;
    }, []);
};

// replacing @Model.LastName format to {LastName} format, it is needed to replace models coming from AngularJS project
const replaceOldPlaceholders = (templateData, groupedTemplateDynamicVariables, templateType) => {
    return keys(templateData).reduce((acc, key) => {
        acc[key] = { ...templateData[key] };
        groupedTemplateDynamicVariables.forEach((item) => {
            if (!(templateType in item.channels)) return;

            const { FieldName, Name } = item.channels[templateType];
            acc[key].Body = replacePlaceholder(acc[key].Body, Name, FieldName);
            if (!isNil(acc[key].Subject)) {
                acc[key].Subject = replacePlaceholder(acc[key].Subject, Name, FieldName);
            }
        });
        return acc;
    }, {});
};

const replacePlaceholder = (source: string, subString: string, replacement: string) => {
    const regex = new RegExp(subString, 'g');
    return source.replace(regex, `{${replacement}}`);
};

const replaceAllLanguagesTopImageUrlPlaceholders = (templateData, groupedTemplateDynamicVariables, templateType) => {
    return keys(templateData).reduce((acc, key) => {
        acc[key] = { ...templateData[key] };

        acc[key].Body = replaceBodyTopImageUrlPlaceholders(
            acc[key].Body,
            groupedTemplateDynamicVariables,
            templateType,
        );
        return acc;
    }, {});
};

const replaceBodyTopImageUrlPlaceholders = (body, groupedTemplateDynamicVariables, templateType) => {
    groupedTemplateDynamicVariables.forEach((item) => {
        if (!(templateType in item.channels)) return;

        const { FieldName, PreviewValue, TypeConfig } = item.channels[templateType];

        if (!isNil(PreviewValue) && TypeConfig?.ValueType === TemplateValueTypeEnum.Image) {
            const regex = new RegExp(`{${FieldName}}`, 'g');
            body = body.replace(regex, PreviewValue);
        }
    });
    return body;
};

const recoverTopImageUrlPlaceholders = (body, groupedTemplateDynamicVariables, templateType) => {
    groupedTemplateDynamicVariables.forEach((item) => {
        if (!(templateType in item.channels)) return;

        const { FieldName, PreviewValue, TypeConfig } = item.channels[templateType];

        if (!isNil(PreviewValue) && TypeConfig?.ValueType === TemplateValueTypeEnum.Image) {
            const regex = new RegExp(PreviewValue, 'g');
            body = body.replace(regex, `{${FieldName}}`);
        }
    });

    return body;
};

interface TemplateInitialDataOptions {
    templateCategoryIds?: string;
    isCascade?: boolean;
}

type CreateTemplateFunction = (
    id: Nullable<string>,
    template?: Nullable<AnyObject>,
    options?: TemplateInitialDataOptions,
) => TemplateInitialData;

type CreateTemplateInitialDataMap = Record<
    Exclude<TemplateTypeEnum, TemplateTypeEnum.CMSBanner>,
    CreateTemplateFunction
>;

const defaultOptions = {} as TemplateInitialDataOptions;

const createTemplateInitialData: CreateTemplateInitialDataMap = {
    SMS: (id = null, template = null, { templateCategoryIds } = defaultOptions) => {
        return {
            Body: template?.Body ?? '',
            TemplateCategoryIds: templateCategoryIds ?? template?.TemplateCategoryIds ?? '',
            Language: id,
        };
    },
    Hoory: (id = null, template = null, { templateCategoryIds } = defaultOptions) => {
        return {
            Body: template?.Body ?? '',
            Subject: template?.Subject ?? '',
            TemplateCategoryIds: templateCategoryIds ?? template?.TemplateCategoryIds ?? '',
            ValidityPeriod: template?.ValidityPeriod ?? HooryTemplateConfig.DefaultValidityPeriodInMinutes,
            Language: id,
            ButtonUrl: template?.ButtonUrl ?? '',
            ButtonText: template?.ButtonText ?? '',
            UploadedImageUrl: template?.UploadedImageUrl ?? '',
            FileName: template?.FileName ?? '',
        };
    },
    Internal: (id = null, template = null, { templateCategoryIds } = defaultOptions) => {
        return {
            Body: template?.Body ?? '',
            TemplateCategoryIds: templateCategoryIds ?? template?.TemplateCategoryIds ?? '',
            Language: id,
            Subject: template?.Subject ?? '',
            ...(template?.UIConfig && { UIConfig: template?.UIConfig }),
        };
    },
    Telegram: (id = null, template = null, { templateCategoryIds } = defaultOptions) => {
        return {
            Body: template?.Body ?? '',
            TemplateCategoryIds: templateCategoryIds ?? template?.TemplateCategoryIds ?? '',
            Language: id,
            InlineKeyboard: template?.InlineKeyboard ?? [[]],
            InlineKeyboardFormikValidity: null,
            UploadedImageUrl: template?.UploadedImageUrl ?? '',
            FileName: template?.FileName ?? '',
        };
    },
    Email: (id = null, template = null, { templateCategoryIds } = defaultOptions) => {
        return {
            Body: template?.Body ?? '',
            TemplateCategoryIds: templateCategoryIds ?? template?.TemplateCategoryIds ?? '',
            Language: id,
            Subject: template?.Subject ?? '',
            ...(template?.UIConfig && { UIConfig: template?.UIConfig }),
        };
    },
    DynamicPopUp: (id = null, template = null, { templateCategoryIds } = defaultOptions) => {
        return {
            Body: template?.Body ?? '',
            TemplateCategoryIds: templateCategoryIds ?? template?.TemplateCategoryIds ?? '',
            Language: id,
            UrlType: template?.UrlType ?? '',
            Url: template?.Url ?? '',
            DeviceType: template?.DeviceType ?? DynamicPopUpDeviceTypesLabels.Both,
            ExpirationType: template?.ExpirationType,
            ExpirationPeriod: template?.ExpirationPeriod,
            ExpirationDate: template?.ExpirationDate,
            ExpirationTime: template?.ExpirationTime,
            Timezone: template?.Timezone,
            ...(template?.UIConfig && { UIConfig: template?.UIConfig }),
        };
    },
    PushNotification: (id = null, template = null, { templateCategoryIds } = defaultOptions) => {
        return {
            Body: template?.Body ?? '',
            TemplateCategoryIds: templateCategoryIds ?? template?.TemplateCategoryIds ?? '',
            Language: id,
            Subject: template?.Subject ?? '',
            TitleOfPushNotification: template?.TitleOfPushNotification ?? '',
            UploadedImageUrl: template?.UploadedImageUrl ?? '',
            FileName: '',
        };
    },
    // TODO: Redux: Please check it this used or dead code, because it has different structure than other templates
    // CustomerJourneyVisualization: (id = null, template = null, { templateCategoryIds } = defaultOptions) => {
    //     return {
    //         Body: template?.Body ?? { name: '', description: '' },
    //         TemplateCategoryIds: templateCategoryIds ?? template?.TemplateCategoryIds ?? '',
    //         Language: id,
    //     };
    // },
    PopUp: (id = null, template = null, { templateCategoryIds } = defaultOptions) => {
        return {
            Body: template?.Body ?? '',
            TemplateCategoryIds: templateCategoryIds ?? template?.TemplateCategoryIds ?? '',
            Language: id,
            TitleOfPopUpNotification: template?.TitleOfPopUpNotification ?? '',
        };
    },
    CMSPopUp: (id = null, template = null, { templateCategoryIds } = defaultOptions) => {
        return {
            Body: template?.Body ?? '',
            TemplateCategoryIds: templateCategoryIds ?? template?.TemplateCategoryIds ?? '',
            Language: id,
        };
    },
    Viber: (id = null, template = null, { isCascade, templateCategoryIds } = defaultOptions) => {
        return {
            Body: template?.Body ?? '',
            TemplateCategoryIds: templateCategoryIds ?? template?.TemplateCategoryIds ?? '',
            ValidityPeriod: template?.ValidityPeriod ?? ViberTemplateConfig.DefaultValidityPeriodInMinutes,
            Language: id,
            ButtonUrl: template?.ButtonUrl ?? '',
            ButtonText: template?.ButtonText ?? '',
            UploadedImageUrl: template?.UploadedImageUrl ?? '',
            FileName: template?.FileName ?? '',
            ...(isCascade && { SMSBody: template?.SMSBody ?? '' }),
            ...(isCascade && { SMSParts: template?.SMSParts ?? '' }),
        };
    },
    WhatsAppProvider: (id = null, template = null, { isCascade, templateCategoryIds } = defaultOptions) => {
        return {
            Body: template?.Body ?? '',
            TemplateCategoryIds: templateCategoryIds ?? template?.TemplateCategoryIds ?? '',
            Language: id,
            ButtonUrl: template?.ButtonUrl ?? '',
            ButtonText: template?.ButtonText ?? '',
            UploadedImageUrl: template?.UploadedImageUrl ?? '',
            FileName: template?.FileName ?? '',
            ...(isCascade && { SMSBody: template?.SMSBody ?? '' }),
            ...(isCascade && { SMSParts: template?.SMSParts ?? '' }),
        };
    },
    WhatsApp: (id = null, template = null, { templateCategoryIds } = defaultOptions) => {
        return {
            Body: '',
            TemplateCategoryIds: templateCategoryIds ?? template?.TemplateCategoryIds ?? '',
            Language: id,
            [TemplateInputTypes.ExternalWhatsAppTemplateId]:
                template?.[TemplateInputTypes.ExternalWhatsAppTemplateId] ?? null,
            [TemplateInputTypes.ExternalWhatsAppTemplateLanguage]:
                template?.[TemplateInputTypes.ExternalWhatsAppTemplateLanguage] ?? null,
            [TemplateInputTypes.ExternalWhatsAppTemplatePlaceholderMapping]:
                template?.[TemplateInputTypes.ExternalWhatsAppTemplatePlaceholderMapping] ?? null,
        };
    },
};

const getTemplateCustomGetLabel = (message) => {
    return message.errorMessage;
};

const getTemplateValidationTranslatableErrorText =
    (t: TFunction) =>
    ({ validationResultValue }) => {
        return getTranslatableErrorText(t, validationResultValue, getTemplateCustomGetLabel);
    };

const getUniqueNameOptions = <T extends { Name: string }>(data: T[]): { label: string; value: string }[] => {
    return chain(data ?? [])
        .map('Name')
        .compact()
        .uniq()
        .map((name) => ({ label: name, value: name }))
        .value();
};

// The AI assistant exchanges dynamic pop-up templates as a stringified JSON envelope rather than
// bare HTML, and it names the platforms instead of using the stored '1' | '2' | '3' device code.
const DynamicPopUpAiPlatforms = {
    [DynamicPopUpDeviceTypesLabels.Desktop]: ['Desktop'],
    [DynamicPopUpDeviceTypesLabels.Mobile]: ['Mobile'],
    [DynamicPopUpDeviceTypesLabels.Both]: ['Desktop', 'Mobile'],
};

const buildDynamicPopUpAiBody = (content: string, deviceType: Nullable<string>): string => {
    return JSON.stringify({ content: content ?? '', platforms: DynamicPopUpAiPlatforms[deviceType ?? ''] ?? [] });
};

/**
 * Reads the html out of the AI envelope. Every other field of the envelope is ignored: the pop-up's
 * url, platforms and expiration stay under the user's control. Falls back to the raw value, since
 * the model can still answer with bare html.
 */
const parseDynamicPopUpAiBody = (body: string): string => {
    let parsed;

    try {
        parsed = JSON.parse(body);
    } catch {
        // Not json at all — the model answered with bare html, so take it as is.
        return body;
    }

    // A well formed envelope that carries no usable content yields nothing, rather than leaking
    // the raw json into the preview and the template body.
    return typeof parsed?.content === 'string' ? parsed.content : '';
};

export {
    buildDynamicPopUpAiBody,
    parseDynamicPopUpAiBody,
    createTemplateResultData,
    groupDynamicVariablesByDisplayName,
    createTemplateDefaultData,
    createTemplateInitialData,
    createLanguageModalData,
    createSelectLanguageModalData,
    createPushNotificationTemplateDefaultData,
    createPushNotificationTemplateResultData,
    createPopUpNotificationTemplateDefaultData,
    createPopUpNotificationTemplateResultData,
    createDynamicPopUpTemplateDefaultData,
    createCMSPopUpNotificationTemplateResultData,
    createCMSPopUpNotificationTemplateDefaultData,
    createCMSBannerTemplateResultData,
    createCMSBannerTemplateDefaultData,
    replaceOldPlaceholders,
    replaceBodyTopImageUrlPlaceholders,
    replaceAllLanguagesTopImageUrlPlaceholders,
    recoverTopImageUrlPlaceholders,
    getTemplateValidationTranslatableErrorText,
    createViberNotificationTemplateResultData,
    createViberNotificationTemplateDefaultData,
    createHooryNotificationTemplateResultData,
    createHooryNotificationTemplateDefaultData,
    createWhatsAppProviderNotificationTemplateResultData,
    createWhatsAppProviderNotificationTemplateDefaultData,
    createWhatsAppNotificationTemplateResultData,
    createWhatsAppNotificationTemplateDefaultData,
    createTelegramNotificationTemplateResultData,
    createTelegramNotificationTemplateDefaultData,
    generatePartialHtml,
    htmlStringToDomElement,
    getUniqueNameOptions,
};
